import {
  getLast7DaysSale,
  getPendingOrders,
  getRecentOrders,
  getRevenueToday,
  getTopCustomers,
  getTotalCustomer,
  getTotalLowStock,
  refreshDashboard,
  getLowStockProducts,
  doReloadConversation,
} from "@/api/dashboard-api";
import { DashboardData } from "@/types/dashboard-type";
import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "./use-toast";

type DashboardError = Partial<Record<keyof DashboardData, string>>;

export function useDashboardData() {
  const [data, setData] = useState<DashboardData>({});
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<DashboardError>({});
  const abortRef = useRef<AbortController | null>(null);
  const refresh = async () => {
    fetchAll();
    refreshDashboard();

  }
  const reloadConversation = async () => {
    const resp = await doReloadConversation();
    toast({
      title: "Success",
      description: "Conversation reloaded successfully",
    });

  }

  const fetchAll = useCallback(async () => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    setLoading(true);
    setErrors({});

    const tasks = {
      revenueToday: getRevenueToday,
      pendingOrders: getPendingOrders,
      topCustomers: getTopCustomers,
      totalLowStock: getTotalLowStock,
      last7DaysSale: getLast7DaysSale,
      recentOrders: getRecentOrders,
      totalCustomer: getTotalCustomer,
      lowStockProducts: getLowStockProducts
    } as const;

    // kick off in parallel
    const entries = Object.entries(tasks) as Array<
      [keyof DashboardData, () => Promise<any>]
    >;

    const results = await Promise.allSettled(
      entries.map(async ([key, fn]) => {
        const res = await fn();
        // res is ApiResponse<T> with shape: { data, success, status, statusText, message, errors }
        // Check if the response was successful
        if (res && typeof res === "object" && "success" in res) {
          if (res.success) {
            return [key, res.data] as const;
          } else {
            // API call succeeded but returned an error response
            throw new Error(res.message || "API returned error");
          }
        }
        // Fallback for unexpected response format
        return [key, res] as const;
      })
    );

    const nextData: DashboardData = {};
    const nextErrors: DashboardError = {};

    for (const r of results) {
      if (r.status === "fulfilled") {
        const [key, value] = r.value;
        (nextData as any)[key] = value;
      } else {
        // figure out which key failed by index
        const idx = results.indexOf(r);
        const [key] = entries[idx];
        nextErrors[key] = r.reason?.message || "Failed to load";
      }
    }

    if (!ac.signal.aborted) {
      setData(nextData);
      setErrors(nextErrors);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    return () => abortRef.current?.abort();
  }, [fetchAll]);


  return { data, errors, loading, refresh: refresh, reloadConversation };
}
